import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { premiumBubbleStyle, premiumWhiteCardStyle } from './allureCardStyles';
import { PopupPortal } from './PopupPortal';
import { useAnchorRect } from '../hooks/useAnchorRect';

export const ProgressCircle = ({
  percent = 0,
  spent = 0,
  saved = 0,
  savingsBreakdown = [],
  target = 100,
  size = 222,
}) => {
  const [isSavingsOpen, setIsSavingsOpen] = useState(false);
  const wrapperRef = useRef(null);
  const buttonRef = useRef(null);
  const buttonRect = useAnchorRect(buttonRef, isSavingsOpen);
  // Show cents when present — a €0.45 saving must display as "0.45€", not
  // round down to a misleading "0€ Saved" (real-payment bug report).
  const formatEuro = (value) => {
    const numeric = Math.max(0, Number(value || 0));
    const rounded = Math.round(numeric * 100) / 100;
    return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(2);
  };
  const savedLabel = formatEuro(saved);
  const strokeWidth = 17;
  const clampedPercent = Math.min(Math.max(percent, 0), 100);
  const progressDeg = (clampedPercent / 100) * 360;
  const fadeZone = Math.min(15, progressDeg * 0.3);

  // Conic gradient: starts at bottom center (180deg), goes clockwise.
  // Clockwise from bottom = LEFT side first, then top, then right.
  // RED goes first (fills left from bottom), then fades into GRAY (unfilled on right).
  const redEnd = Math.max(0, progressDeg - fadeZone);

  const gradient = clampedPercent > 0
    ? `conic-gradient(from 180deg, #DD0608 0deg, #DD0608 ${redEnd}deg, var(--progress-track) ${progressDeg}deg, var(--progress-track) 360deg)`
    : `conic-gradient(from 180deg, var(--progress-track) 0deg, var(--progress-track) 360deg)`;

  const formatSavedAmount = (value) => formatEuro(value);

  const formatSavedDate = (value) => {
    if (!value) {
      return '';
    }

    const parsedDate = new Date(value);
    if (Number.isNaN(parsedDate.getTime())) {
      return '';
    }

    return new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short' }).format(parsedDate);
  };

  useEffect(() => {
    if (!isSavingsOpen) {
      return undefined;
    }

    const handlePointerDown = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsSavingsOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
    };
  }, [isSavingsOpen]);

  return (
    <div ref={wrapperRef} style={{ width: size, height: size, position: 'relative' }}>
      {/* Outer shadow + dark-mode white stroke */}
      <div
        style={{
          position: 'absolute',
          inset: -2,
          borderRadius: '50%',
          boxShadow: 'var(--premium-shadow-nested)',
          border: '1.5px solid var(--card-border)',
        }}
      />

      {/* Ring using conic-gradient + inner mask */}
      <div
        style={{
          width: size,
          height: size,
          borderRadius: '50%',
          background: gradient,
          position: 'relative',
        }}
      >
        {/* Inner white circle (creates the donut/ring) */}
        <div
          style={{
            position: 'absolute',
            top: strokeWidth,
            left: strokeWidth,
            right: strokeWidth,
            bottom: strokeWidth,
            borderRadius: '50%',
            backgroundColor: 'var(--surface)',
            boxShadow: 'inset 2px 3px 6px var(--inner-shadow)',
          }}
        />
      </div>

      {/* Center text */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <span
          style={{
            fontFamily: "'Inria Serif', serif",
            fontSize: 64,
            fontWeight: 400,
            color: 'var(--text)',
            lineHeight: 1,
            textShadow: '1px 2px 4px var(--inner-shadow)',
          }}
        >
          {Math.round(clampedPercent)}%
        </span>
        <span
          style={{
            fontFamily: "'Inria Serif', serif",
            fontSize: 14,
            fontWeight: 400,
            color: 'var(--text-2)',
            marginTop: 4,
          }}
        >
          {Math.round(spent)}&euro; / {Math.round(target)}&euro;
        </span>
        <button
          ref={buttonRef}
          type="button"
          data-testid="guest-progress-saved-button"
          onClick={() => setIsSavingsOpen((currentValue) => !currentValue)}
          style={{
            ...premiumWhiteCardStyle,
            borderRadius: 999,
            padding: '4px 10px',
            marginTop: 4,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'var(--surface)',
            cursor: 'pointer',
          }}
        >
          <span
            data-testid="guest-progress-saved-amount"
            style={{
              fontFamily: "'Inria Serif', serif",
              fontSize: 14,
              fontWeight: 700,
              color: 'var(--status-success-text)',
              }}
          >
            {savedLabel}&euro; Saved
          </span>
        </button>
      </div>

      <AnimatePresence>
        {isSavingsOpen && buttonRect ? (
          <PopupPortal>
            <div
              style={{
                position: 'fixed',
                top: buttonRect.bottom + 10,
                left: buttonRect.left + buttonRect.width / 2,
                transform: 'translateX(-50%)',
                width: 'min(270px, calc(100vw - 40px))',
              }}
            >
            <motion.div
              aria-hidden="true"
              initial={{ opacity: 0, y: -6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.18, ease: 'easeOut' }}
              style={{
                width: 18,
                height: 18,
                position: 'absolute',
                top: -8,
                left: '50%',
                marginLeft: -9,
                transform: 'rotate(45deg)',
                ...premiumBubbleStyle,
                borderBottom: 'none',
                borderRight: 'none',
              }}
            />
            <motion.div
              data-testid="guest-progress-saved-bubble"
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.2, ease: 'easeOut' }}
              style={{
                ...premiumBubbleStyle,
                position: 'relative',
                padding: '14px 16px',
                display: 'grid',
                gap: 8,
              }}
            >
              <p style={{ fontFamily: "'Inria Serif', serif", fontSize: 16, fontWeight: 700, color: 'var(--text)', lineHeight: 1.1, margin: 0 }}>
                Your recent savings
              </p>
              {savingsBreakdown.length ? savingsBreakdown.map((item) => (
                <div key={item.transaction_id || `${item.venue_name}-${item.paid_at}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                  <div style={{ minWidth: 0 }}>
                    <p style={{ fontFamily: "'Inria Serif', serif", fontSize: 14, color: 'var(--text)', margin: 0, lineHeight: 1.15, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {item.venue_name || 'Partner venue'}
                    </p>
                    <p style={{ fontFamily: "'Inria Serif', serif", fontSize: 12, color: 'var(--text-3)', margin: '2px 0 0', lineHeight: 1.1 }}>
                      {formatSavedDate(item.paid_at)}
                    </p>
                  </div>
                  <p style={{ fontFamily: "'Inria Serif', serif", fontSize: 14, fontWeight: 700, color: 'var(--status-success-text)', margin: 0, flexShrink: 0 }}>
                    {formatSavedAmount(item.saved_amount)}&euro;
                  </p>
                </div>
              )) : (
                <p style={{ fontFamily: "'Inria Serif', serif", fontSize: 14, color: 'var(--text-3)', lineHeight: 1.2, margin: 0 }}>
                  We’ll show your saved-amount breakdown after your discounted visits.
                </p>
              )}
            </motion.div>
          </div>
          </PopupPortal>
        ) : null}
      </AnimatePresence>
    </div>
  );
};

export default ProgressCircle;
